import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import * as mentorApi from '../../api/mentorApi';

const GENERIC_ERROR = "Couldn't complete that - please try again.";

const ROLE_LABELS = { program_administrator: 'Program Administrator', spoc: 'SPOC', auditor: 'Auditor' };

const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : '-');
const roleLabel = (role) => ROLE_LABELS[role] || role || '';
const joinList = (items) => (Array.isArray(items) && items.length > 0 ? items.join(', ') : '-');

// Approval cannot be undone (the student's certificate is issued from it), so it is confirmed first.
function ApproveConfirmDialog({ certificate, onCancel, onConfirm }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  // Rendered into document.body: the screen sits inside an animated (transformed) container, which would otherwise
  // become the containing block of this fixed overlay and put the dialog far from the viewport.
  return createPortal(
    <div className="modal-overlay" onClick={onCancel}>
      <div
        className="modal-card esg-confirm-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="esg-confirm-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="esg-confirm-icon" aria-hidden="true">!</div>
        <h3 id="esg-confirm-title">Approve this certificate?</h3>
        <p className="esg-confirm-text">
          {`You are about to approve the ${certificate.level?.name || 'ESG'} certificate for ${certificate.student?.name || 'this student'} `}
          {`(final score ${certificate.overall_score}/100).`}
        </p>
        <p className="esg-confirm-warning">Once approved, the certificate is issued to the student and this cannot be undone.</p>
        <div className="esg-confirm-actions">
          <button type="button" className="esg-btn esg-btn-secondary" onClick={onCancel}>Cancel</button>
          <button type="button" className="esg-approve-btn esg-approve-btn--compact" onClick={onConfirm} autoFocus>Yes, approve</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function taskText(tasks) {
  if (!Array.isArray(tasks) || tasks.length === 0) return '-';
  return tasks
    .map((t) => `${t.task_key}: ${t.confirmed ? `${t.rating ?? '-'}/10` : 'not confirmed'}`)
    .join('; ');
}

// Everything LMS knows about the student for this level, council by council, so the approver can judge before approving.
function CertificateDetail({ certificate, busy, onApprove, onBack }) {
  const report = certificate.report || {};
  const scores = report.scores || {};
  const rows = Array.isArray(report.council_rows) ? report.council_rows : [];
  const pending = certificate.status === 'pending';
  const [confirming, setConfirming] = useState(false);
  const cancelConfirm = useCallback(() => setConfirming(false), []);

  return (
    <div className="placeholder-card">
      <button type="button" className="esg-btn esg-btn-secondary" onClick={onBack}>Back to list</button>
      <h3>{`${certificate.student?.name || 'Student'} - ${certificate.level?.name || 'Level'}`}</h3>
      <p>{`Final score ${certificate.overall_score}/100`}</p>

      <table className="students-table" aria-label="Score components">
        <thead>
          <tr><th>Component</th><th>Weight</th><th>Score</th></tr>
        </thead>
        <tbody>
          <tr><td>MCQ exam</td><td>{scores.weights?.exam ?? 30}%</td><td>{scores.exam ?? '-'}</td></tr>
          <tr><td>Social startup</td><td>{scores.weights?.startup ?? 35}%</td><td>{scores.startup ?? '-'}</td></tr>
          <tr><td>Case studies</td><td>{scores.weights?.cases ?? 35}%</td><td>{scores.cases ?? '-'}</td></tr>
        </tbody>
      </table>

      <h4 className="esg-certificates__h4-1">Councils</h4>
      <div className="students-table-card">
        <table className="students-table" aria-label="Council details">
          <thead>
            <tr>
              <th>Council</th>
              <th>Own rating</th>
              <th>Auditor: session</th>
              <th>Auditor: student</th>
              <th>Strengths</th>
              <th>Coaching areas</th>
              <th>Tasks</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={7}>No council details were sent.</td></tr>
            ) : (
              rows.map((r) => (
                <tr key={r.council?.council_number}>
                  <td>{`${r.council?.council_number}. ${r.council?.title || ''}`}</td>
                  <td>{r.self_rating ?? '-'}</td>
                  <td>{r.session_rating ?? '-'}</td>
                  <td>{r.my_member?.rating ?? r.chair?.rating ?? '-'}</td>
                  <td>{joinList([...(r.session_strengths || []), ...(r.my_member?.strengths || [])])}</td>
                  <td>{joinList([...(r.session_coaching || []), ...(r.my_member?.coaching || [])])}</td>
                  <td>{taskText(r.my_tasks)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pending ? (
        <div className="esg-approve-wrap">
          <button type="button" className="esg-approve-btn" disabled={busy} onClick={() => setConfirming(true)}>
            <svg className="esg-approve-btn__icon" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="9.5" />
              <path d="M7.8 12.4l3 3 5.4-6" />
            </svg>
            <span>{busy ? 'Approving...' : 'Approve certificate'}</span>
          </button>
        </div>
      ) : (
        <p className="esg-certificates__p-2">
          {`Approved by ${certificate.approved_by_name || 'unknown'}${certificate.approved_by_role ? ` (${roleLabel(certificate.approved_by_role)})` : ''} on ${formatDate(certificate.approved_at)}`}
        </p>
      )}
      {confirming && (
        <ApproveConfirmDialog
          certificate={certificate}
          onCancel={cancelConfirm}
          onConfirm={() => { setConfirming(false); onApprove(); }}
        />
      )}
    </div>
  );
}

// ESG certificates: students whose weighted score is at least 40. The certificate is issued in LMS only after an
// auditor, SPOC or program administrator approves it here; who approved is kept.
export default function EsgCertificates({ onError, onSuccess }) {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await mentorApi.getEsgCertificates(status ? { status } : {}));
    } catch (err) {
      onError?.("Couldn't load certificates - try again.");
    } finally {
      setLoading(false);
    }
  }, [status, onError]);

  useEffect(() => {
    load();
  }, [load]);

  const open = async (id) => {
    setFormError('');
    try {
      setSelected(await mentorApi.getEsgCertificate(id));
    } catch (err) {
      onError?.("Couldn't load that certificate - try again.");
    }
  };

  const approve = async () => {
    setBusy(true);
    setFormError('');
    try {
      await mentorApi.approveEsgCertificate(selected.id);
      setSelected(null);
      await load();
      onSuccess?.('Certificate approved.');
    } catch (err) {
      setFormError(GENERIC_ERROR);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="dashboard-title-row">
        <h1>ESG Certificates</h1>
      </div>
      {formError && <div className="placeholder-card" role="alert">{formError}</div>}
      {selected ? (
        <CertificateDetail certificate={selected} busy={busy} onApprove={approve} onBack={() => setSelected(null)} />
      ) : (
        <>
          <div className="form-field-group esg-certificates__div-3">
            <label htmlFor="esg-cert-status">Show</label>
            <select id="esg-cert-status" className="form-control-select" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="pending">Waiting for approval</option>
              <option value="approved">Approved</option>
              <option value="">All</option>
            </select>
          </div>
          {loading ? (
            <div className="placeholder-card">Loading certificates...</div>
          ) : items.length === 0 ? (
            <div className="placeholder-card">No certificates to show.</div>
          ) : (
            <div className="students-table-card">
              <table className="students-table">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Level</th>
                    <th>Score</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {items.map((c) => (
                    <tr key={c.id}>
                      <td>{c.student?.name}</td>
                      <td>{c.level?.name}</td>
                      <td>{`${c.overall_score}/100`}</td>
                      <td>
                        {c.status === 'approved'
                          ? `Approved by ${c.approved_by_name || 'unknown'} on ${formatDate(c.approved_at)}`
                          : 'Waiting for approval'}
                      </td>
                      <td>
                        <button type="button" className="esg-btn esg-btn-secondary" onClick={() => open(c.id)}>
                          View details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
